import {
  ApolloClient,
  createHttpLink,
  InMemoryCache,
  from,
  fromPromise,
} from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { getUser } from '~src/user';
import { RetryLink } from '@apollo/client/link/retry';
import { onError } from '@apollo/client/link/error';

let graphQlClient;

const retryLink = new RetryLink({
  delay: {
    initial: 300,
    max: Infinity,
    jitter: true,
  },
  attempts: {
    max: 5,
    retryIf: (error) => {
      // Session recovery belongs to sessionErrorLink, never to transport retry.
      const code = Number(error?.result?.code ?? error?.result?.error?.code);
      if (code === 209) return false;
      const status = error?.statusCode ?? error?.status;
      return !!error && (!status || status >= 500);
    },
  },
});

const getGraphQLClient = async () => {
  if (graphQlClient) {
    return graphQlClient;
  }

  const httpLink = createHttpLink({
    uri: process.env.GRAPHQL_ENDPOINT,
  });

  const sessionErrorLink = onError(
    ({ graphQLErrors, networkError, operation, forward }) => {
      const codes = [
        networkError?.result?.code,
        networkError?.result?.error?.code,
        ...(graphQLErrors || []).map((error) => error.extensions?.code),
      ];
      if (
        !codes.some((code) => Number(code) === 209) ||
        operation.getContext().parseSessionRetried
      )
        return;
      operation.setContext({ parseSessionRetried: true });
      // /me owns the bounded renewal. Re-running the auth link uses its new token.
      return fromPromise(getUser({}, true)).flatMap(() => forward(operation));
    }
  );

  const authLink = setContext(async (_, { headers }) => {
    const { userToken } = await getUser();
    return {
      headers: {
        ...headers,
        'x-parse-application-id': process.env.PARSE_APPLICATION_ID,
        'x-parse-session-token': userToken,
      },
    };
  });

  graphQlClient = new ApolloClient({
    // Retry only HTTP transport. Authentication failures must escape unchanged.
    link: from([sessionErrorLink, authLink, retryLink, httpLink]),
    cache: new InMemoryCache({ resultCaching: false }),
  });

  return graphQlClient;
};

export default getGraphQLClient;
